import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Permission, Role } from "@salon/shared";

export type AuthUser = {
  id: string;
  username: string;
  role: Role;
  employeeId: string | null;
};

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: number; // ms epoch
  refreshExpiresAt: number; // ms epoch
};

const EMPTY_TOKENS = {
  accessToken: null,
  refreshToken: null,
  accessExpiresAt: null,
  refreshExpiresAt: null,
} as const;

type AuthState = {
  user: AuthUser | null;
  // null = admin (all permissions); array = explicit list
  permissions: Permission[] | null;
  accessToken: string | null;
  refreshToken: string | null;
  accessExpiresAt: number | null;
  refreshExpiresAt: number | null;
  setAuth: (user: AuthUser | null, permissions: Permission[] | null) => void;
  setUser: (u: AuthUser | null) => void;
  setTokens: (t: AuthTokens | null) => void;
  hasPermission: (key: Permission) => boolean;
  clear: () => void;
};

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      permissions: null,
      accessToken: null,
      refreshToken: null,
      accessExpiresAt: null,
      refreshExpiresAt: null,
      setAuth: (user, permissions) => set({ user, permissions }),
      setUser: (user) => set({ user }),
      setTokens: (t) => set(t ?? EMPTY_TOKENS),
      clear: () => set({ user: null, permissions: null, ...EMPTY_TOKENS }),
      hasPermission: (key) => {
        const { user, permissions } = get();
        if (!user) return false;
        if (user.role === "admin") return true;
        if (permissions === null) return false;
        return permissions.includes(key);
      },
    }),
    { name: "salon-auth" },
  ),
);
